import { z } from 'zod';
import { passwordSchema } from './auth.js';
export const USER_STATUSES = ['ACTIVE', 'DISABLED', 'INVITED'];
/**
 * Employee code is the human identifier used across Excel imports and reports,
 * so it is normalised to upper case at the edge — "pp-1024" and "PP-1024" must
 * never become two different people.
 */
export const employeeCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .min(2, 'Employee code must be at least 2 characters')
    .max(40)
    .regex(/^[A-Z0-9][A-Z0-9._-]*$/, 'Use letters, numbers, dot, underscore or hyphen');
/**
 * The same rule, for a field nobody has to fill in.
 *
 * Not everyone has a staff number — contractors, visiting trainers and new
 * joiners whose code has not been issued yet — and the form used to refuse to
 * create them at all.
 *
 * An empty string becomes null rather than "": the column is unique, and a
 * second empty string would collide with the first, so the first codeless
 * person created would be the only one. Null does not collide with null.
 */
export const optionalEmployeeCodeSchema = z
    .union([employeeCodeSchema, z.literal(''), z.null(), z.undefined()])
    .transform((value) => (value === '' || value === undefined ? null : value))
    .nullable();
export const createUserSchema = z.object({
    employeeCode: optionalEmployeeCodeSchema,
    email: z.string().trim().toLowerCase().email('Enter a valid email address').max(200),
    firstName: z.string().trim().min(1, 'First name is required').max(80),
    lastName: z.string().trim().min(1, 'Last name is required').max(80),
    phone: z
        .string()
        .trim()
        .max(20)
        .regex(/^[+()\d\s-]*$/, 'Enter a valid phone number')
        .optional()
        .or(z.literal('')),
    departmentId: z.string().uuid().optional().nullable(),
    designationId: z.string().uuid().optional().nullable(),
    roleIds: z.array(z.string().uuid()).min(1, 'Assign at least one role'),
    /** Departments this person MANAGES — distinct from the one they belong to. */
    managedDepartmentIds: z.array(z.string().uuid()).default([]),
    joinedAt: z.coerce.date().optional().nullable(),
    /** Omit to auto-generate a temporary password the user must change. */
    password: passwordSchema.optional(),
});
export const updateUserSchema = createUserSchema.partial().omit({ password: true });
export const setUserStatusSchema = z.object({
    status: z.enum(['ACTIVE', 'DISABLED']),
});
export const resetUserPasswordSchema = z.object({
    /** Omit to generate one and force a change at next sign-in. */
    password: passwordSchema.optional(),
});
export const userListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    search: z.string().trim().max(200).optional(),
    departmentId: z.string().uuid().optional(),
    designationId: z.string().uuid().optional(),
    roleId: z.string().uuid().optional(),
    status: z.enum(USER_STATUSES).optional(),
    sortBy: z.enum(['firstName', 'employeeCode', 'email', 'createdAt', 'lastLoginAt']).default('firstName'),
    sortDir: z.enum(['asc', 'desc']).default('asc'),
});
// ── Departments ────────────────────────────────────────────────────────────
export const createDepartmentSchema = z.object({
    name: z.string().trim().min(2, 'Name is required').max(120),
    /**
     * Optional — derived from the name when it is left out.
     *
     * The code is a short stable handle for bulk imports and reports, and is
     * genuinely useful there. It is friction everywhere else: somebody adding
     * "Field Operations" from the user form has no opinion about its code and
     * should not have to invent one. See `lib/org-code.ts`; deriving it on the
     * server keeps one implementation and lets the uniqueness check happen where
     * the rows actually are.
     */
    code: z
        .string()
        .trim()
        .toUpperCase()
        .min(2, 'Code is required')
        .max(20)
        .regex(/^[A-Z0-9-]+\$/, 'Use letters, numbers or hyphen')
        .optional(),
    description: z.string().trim().max(500).optional().nullable(),
    isActive: z.boolean().default(true),
});
export const updateDepartmentSchema = createDepartmentSchema.partial();
// ── Designations ───────────────────────────────────────────────────────────
export const createDesignationSchema = z.object({
    name: z.string().trim().min(2, 'Name is required').max(120),
    /**
     * Optional — derived from the name when it is left out.
     *
     * The code is a short stable handle for bulk imports and reports, and is
     * genuinely useful there. It is friction everywhere else: somebody adding
     * "Field Operations" from the user form has no opinion about its code and
     * should not have to invent one. See `lib/org-code.ts`; deriving it on the
     * server keeps one implementation and lets the uniqueness check happen where
     * the rows actually are.
     */
    code: z
        .string()
        .trim()
        .toUpperCase()
        .min(2, 'Code is required')
        .max(20)
        .regex(/^[A-Z0-9-]+\$/, 'Use letters, numbers or hyphen')
        .optional(),
    description: z.string().trim().max(500).optional().nullable(),
    isActive: z.boolean().default(true),
});
export const updateDesignationSchema = createDesignationSchema.partial();
// ── Roles ──────────────────────────────────────────────────────────────────
export const createRoleSchema = z.object({
    name: z.string().trim().min(2, 'Name is required').max(80),
    description: z.string().trim().max(500).optional().nullable(),
    permissionKeys: z.array(z.string().trim().min(1)).default([]),
});
export const updateRoleSchema = createRoleSchema.partial();
export const setRolePermissionsSchema = z.object({
    permissionKeys: z.array(z.string().trim().min(1)),
});
//# sourceMappingURL=user.js.map